import { captureException } from '@sentry/react';
import { ServiceHttpError } from 'base/service_error';
import { formatError } from 'base/ui/form';
import { LLMUserFacingError } from 'blocks/llm_proxy_service';
import {
  ChangeBuffer,
  EntryAlreadyUndoneError,
  UndoManager,
} from 'blocks/woot_woot/undo';
import { computed, makeObservable, observable, when } from 'mobx';
import { computedFn } from 'mobx-utils';
import React from 'react';
import { EditorMode } from '../editor_mode';
import { Page, Section } from '../model';
import { ProjectSession } from '../session/session';

export type TaskMeta = {
  icon: React.ComponentType;
  titleWhenRunning: string | (() => string);
  /** "AI is ..." */
  thirdPersonTooltip: string;
  /** "Error ..." */
  errorTooltip: string;
  onlyNotifyIn?: EditorMode | 'never';
  affects: readonly (Page | Section)[];
};

export type TaskContext = {
  buffer: ChangeBuffer;
  abortSignal: AbortSignal;
  onGetUsageId: (id: number) => void;
};

export const YOU_NEED_TO_LOG_IN = 'You need to log in to continue';

export class LLMErrorFormatter {
  constructor(private readonly session: Pick<ProjectSession, 'showAuthWall'>) {}

  /**
   * Formats the error message & shows the login dialog if needed
   **/
  process(error: unknown): string {
    const showAuthWall =
      error instanceof ServiceHttpError &&
      error.status === 403 &&
      (error.message.includes('unauthenticated use limit exceeded') ||
        error.message.includes('not logged in'))
        ? this.session.showAuthWall
        : undefined;
    if (showAuthWall) {
      showAuthWall();
      return YOU_NEED_TO_LOG_IN;
    }

    const rateLimitError =
      error instanceof ServiceHttpError && error.status === 429;
    if (rateLimitError) {
      return "You've reached the limit for AI requests. Try again in a few minutes.";
    }

    if (error instanceof LLMUserFacingError) {
      return error.userMessage;
    }
    //
    // Server errors are logged to Sentry, only log non-500s
    if (
      !(error instanceof ServiceHttpError) ||
      error.status < 500 ||
      error.status > 599
    ) {
      console.error('Ai task error', error);
      captureException(error);
    }
    return `Please try again later. ${formatError(error)}`;
  }
}

export class AiTask {
  completed:
    | Readonly<{ success: true }>
    | Readonly<{
        success: false;
        errorText: string;
      }>
    | undefined = undefined;
  readonly promise: Promise<void>;
  readonly llmUsageIds = observable.array<number>();

  private readonly abort = new AbortController();

  constructor(
    readonly meta: TaskMeta,
    private readonly errorFormatter: LLMErrorFormatter,
    run: (ctx: TaskContext) => Promise<void>,
    readonly removeFromLog: () => void,
    buffer: ChangeBuffer,
  ) {
    makeObservable(this, {
      completed: observable.ref,
    });

    this.promise = run({
      buffer,
      abortSignal: this.abort.signal,
      onGetUsageId: (id: number) => this.llmUsageIds.push(id),
    }).then(() => {
      this.completed = { success: true };
    });
    this.promise.catch(this.handleTaskError);
  }

  private handleTaskError = (error: unknown) => {
    if (this.completed) throw new Error();
    // Don't treat a (user initiated) stop as an error:
    if (
      this.abort?.signal?.aborted ||
      error instanceof EntryAlreadyUndoneError
    ) {
      this.completed = { success: true };
    } else {
      this.completed = {
        success: false,
        errorText: this.errorFormatter.process(error),
      };
    }
  };

  /** Resolves when the task is stopped */
  readonly stop = async () => {
    if (this.completed) return;
    const completed = when(() => this.completed != null, {
      timeout: 1000,
    });
    this.abort.abort();
    await completed;
    if (!this.completed) {
      throw new Error('task not completed after abort signal');
    }
  };
}

export class AiTaskLog {
  readonly tasks: AiTask[] = observable.array([], { deep: false });

  constructor(
    private readonly undo: UndoManager,
    private readonly errorFormatter: LLMErrorFormatter,
  ) {
    makeObservable(this);
  }

  /** Returns the list of tasks affecting a target.  (pending or errored) */
  readonly tasksAffecting = computedFn((target: Page | Section) =>
    this.tasks.filter((task) => task.meta.affects.includes(target)),
  );

  @computed
  get pendingTasks() {
    return this.tasks.filter((task) => !task.completed);
  }

  @computed
  get affectedByPending() {
    return new Set(this.pendingTasks.flatMap((task) => task.meta.affects));
  }

  @computed
  get failedTasks() {
    return new Set(
      this.tasks.filter((task) => task.completed?.success === false),
    );
  }

  public startTask(
    {
      useExistingUndoEntry,
      ...meta
    }: TaskMeta & { useExistingUndoEntry?: ChangeBuffer },
    run: (ctx: TaskContext) => Promise<void>,
  ): AiTask {
    const undoEntry = useExistingUndoEntry || this.undo.createBuffer();
    const task: AiTask = new AiTask(
      meta,
      this.errorFormatter,
      run,
      () => arrayDelete(this.tasks, task),
      undoEntry,
    );
    this.tasks.unshift(task);
    task.promise.finally(() => {
      // Leave errored tasks in the list, so the user can click to view the error
      if (task.completed?.success) {
        arrayDelete(this.tasks, task);
      }
    });
    return task;
  }

  removeErroredTask(task: AiTask) {
    if (task.completed?.success !== false) throw new Error('task not errored');
    arrayDelete(this.tasks, task);
  }
}

function arrayDelete<T>(arr: T[], item: T) {
  const index = arr.indexOf(item);
  if (index === -1) throw new Error('item not found');
  arr.splice(index, 1);
}
